import { useContext, useEffect, useState, useRef } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import useWebSocket from "react-use-websocket";
import { makeStyles } from "tss-react/mui";
import { useTranslation } from "react-i18next";
import DashboardApi from "./dashboardApi";
import ModuleMonitoringPage from "./pages/module";
import TabPanel from "src/app/components/tab-panel";
import appContext from "src/app/context";
import * as dateFns from "date-fns";
import { buildData, buildSoketData } from "src/app/utils/helper";
import api from "../../app/api";
import { USER_NAME_ROLE } from "src/app/auth";

const useStyles = makeStyles()((theme) => {
  return {
    trendsTabRoot: {
      minHeight: "auto",
    },
    tabsRoot: {
      height: "34px",
      minHeight: "34px",
    },
    tabRoot: {
      background: "#fff",
      borderRadius: "4px",
      border: "1px solid #f7f7f7",
      height: "34px",
      minHeight: "34px",
      "&.Mui-selected": {
        background: theme.palette.primary.main,
        color: "#fff",
      },
    },
    tabIndicator: {
      display: "none",
    },
    activeTab: {
      background: theme.palette.primary.main,
      color: "#fff",
    },
  };
});

export interface IActiveModule {
  name: string;
  index: number;
}

function tabProps(index: number) {
  return {
    id: `dashboard-module-tab-${index}`,
    "aria-controls": `dashboard-module-tabpanel-${index}`,
  };
}

const TabModuleRender = ({
  moduleId,
  configurationId,
  type,
  moduleData,
  classes,
  trendsData,
  trendsDays,
  onTrendsDaysChange,
  processName,
  formData,
  moduleType,
  status
}: any) => {
  const { t } = useTranslation();

  switch (type) {
    case "Engine":
    case "Torque":
    case "Turbine":
    case "Motor":
    case "Bearing":
    case "Turbocharger":
    case "Propeller":
      return (
        <Box>
          <ModuleMonitoringPage
            moduleId={moduleId}
            configurationId={configurationId}
            moduleData={moduleData}
            classes={classes}
            trendsData={trendsData}
            trendsDays={trendsDays}
            onTrendsDaysChange={onTrendsDaysChange}
            processName={processName}
            formData={formData}
            moduleType={moduleType}
            status={status}
          />
        </Box>
      );
    default:
      return (
        <Box>
          <Typography component="span" variant="body1">
            {t("dashboard.type.not.supported", { ns: "dashboard" })}
          </Typography>{" "}
        </Box>
      );
  }
};

const DashboardPage = () => {
  const [moduleTabs, setModuleTabs] = useState<any[]>([]);
  const [webSocketsData, setWebSocketsData] = useState({});
  const [isDataAvailable, setIsDataAvailable] = useState<any>(undefined);
  const [trendsData, setTrendsData] = useState({});
  const [trendsDays, setTrendsDays] = useState<1 | 7>(1);
  const trendsTimingRef = useRef<{
    moduleId: any;
    days: 1 | 7;
    requestMs: number;
    buildDataMs: number;
    responseReceivedAt: number;
    processedAt: number;
    historicalRows: number;
  } | null>(null);
  const [isWebsocketConnect, setIsWebSocketConnect] = useState(true);
  const [isWebSocketFailed, setIsWebSocketFailed] = useState(false);
  const [activeModule, setActiveModule] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showLicenseExpiryMsg, setShowLicenseExpiryMsg] =
    useState<boolean>(false);
  const [licExpiryText, setLicExpiryText] = useState<string>("");
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { licenseInfo, licenseStatus } = useContext(appContext);
  const intervalHandle = useRef();
  const [moduleStatus, setModuleStatus] = useState<number>(0);
  const [rebootConfirmOpen, setRebootConfirmOpen] = useState(false);
  const [isSystemRebootLoading, setIsSystemRebootLoading] = useState(false);
  const { sendMessage, lastMessage } = useWebSocket(
    process.env.REACT_APP_WEBSOCKET_URL ||
    `wss://${window.location.hostname}/wss1`,
    {
      onMessage: (e) => {
        // console.log("message", e);
        if (sendMessage) sendMessage(moduleTabs[activeModule].process_name);
        setIsWebSocketFailed(false);
      },
      onError: (e) => {
        // console.log("error", e);
        // console.log("T", "Error triggered");
        // console.log(process.env.REACT_APP_API_URL, window.location.origin);

        let websocketUrl =
          process.env.REACT_APP_API_URL ?? window.location.origin;
        websocketUrl = websocketUrl.replace("public/", "");

        // console.log(websocketUrl);

        let abortController = new AbortController();
        const timeout = setTimeout(() => {
          abortController.abort();
          // console.log("Request aborted");
        }, 3000);

        Promise.all([
          api.get(`${websocketUrl}app/start_dashboard_socket.php`, {
            signal: abortController.signal,
          }),
          api.get(`${websocketUrl}app/start_signal_socket.php`, {
            signal: abortController.signal,
          }),
          api.get(`${websocketUrl}app/start_status_socket.php`, {
            signal: abortController.signal,
          }),
        ])
          .then(() => {
            clearTimeout(timeout);
            // console.log("T", "Success");
            // window.location.reload();
          })
          .catch((e) => {
            if (e.message === "canceled") {
              // console.log("T", "Cancelled");
              // window.location.reload();
            } else {
              console.error(
                "Failed to start websockets, maybe they are already running?"
              );
            }
          });

        // setIsDataAvailable(
        //   "Failed to connect web socket server. Please try to start websocket by clicking this button."
        // );
        // setIsWebSocketFailed(true);
        // setIsLoading(false);
      },
      shouldReconnect: (closeEvent) => true,
      retryOnError: true,
      reconnectAttempts: 10,
      reconnectInterval: 5000,
      onReconnectStop: (e) => {
        console.log("reconnect stopped", e);
        setIsDataAvailable(
          "Failed to connect web socket server. Please try to start websocket by clicking this button."
        );
        setIsWebSocketFailed(true);
        setIsLoading(false);
      },
    },
    isWebsocketConnect
  );

  useEffect(() => {
    return () => setIsWebSocketConnect(false);
  }, []);
  useEffect(() => {
    if (moduleTabs.length > 0) {
      if (moduleTabs[activeModule]?.process_name) {
        setIsLoading(true);
        setWebSocketsData({});
        setTrendsData({});
        sendMessage(moduleTabs[activeModule].process_name);
      } else {
        setIsLoading(false);
      }
    }
  }, [moduleTabs, activeModule]);

  useEffect(() => {
    const activeModuleData = moduleTabs[activeModule];
    if (!activeModuleData?.process_name) return;

    const moduleId = activeModuleData.id;
    dashBoardApiCall(moduleId, trendsDays);
    const interval = setInterval(() => {
      dashBoardApiCall(moduleId, trendsDays);
    }, 1 * 60 * 1000);

    return () => clearInterval(interval);
  }, [moduleTabs, activeModule, trendsDays]);

  function dashBoardApiCall(datas: any, days: 1 | 7) {
    const shouldMeasure = process.env.NODE_ENV === "development";
    const requestStartedAt = shouldMeasure ? performance.now() : 0;

    DashboardApi.getTrendsData(datas, days)
      .then((val) => {
        const responseReceivedAt = shouldMeasure ? performance.now() : 0;
        const parsedData = buildData(val);

        if (shouldMeasure) {
          const processedAt = performance.now();
          trendsTimingRef.current = {
            moduleId: datas,
            days,
            requestMs: responseReceivedAt - requestStartedAt,
            buildDataMs: processedAt - responseReceivedAt,
            responseReceivedAt,
            processedAt,
            historicalRows: val?.historical_data?.length || 0,
          };
        }

        setTrendsData(parsedData);
      })
      .catch((e) => {
        // console.error(e);
        setTrendsData({});
      });
  }

  useEffect(() => {
    const timing = trendsTimingRef.current;
    if (!timing) return;

    const reactUpdatedAt = performance.now();
    console.table({
      moduleId: timing.moduleId,
      days: timing.days,
      requestMs: Number(timing.requestMs.toFixed(1)),
      buildDataMs: Number(timing.buildDataMs.toFixed(1)),
      reactUpdateMs: Number((reactUpdatedAt - timing.processedAt).toFixed(1)),
      responseToUpdateMs: Number(
        (reactUpdatedAt - timing.responseReceivedAt).toFixed(1)
      ),
      historicalRows: timing.historicalRows,
    });
    trendsTimingRef.current = null;
  }, [trendsData]);

  useEffect(() => {
    if (lastMessage) {
      const data = lastMessage?.data;
      // console.log("Received WebSocket Data:", data); // Add this to inspect raw data
      if (data) {
        try {
          let parsedData = JSON.parse(data);
          // console.log("Parsed Data:", parsedData); // Log parsed data for debugging

          if (parsedData?.Status === "Failed") {
            setIsDataAvailable(parsedData?.Message);
            setIsWebSocketFailed(false);
            setWebSocketsData({});
          } else {
            parsedData = buildSoketData(
              parsedData,
              moduleTabs[activeModule]?.module_type, // Ensure module_type is available
              moduleTabs[activeModule]?.from_data  // Ensure from_data is available
            );
            // console.log("Processed Data:", parsedData); // Log final processed data
            setIsDataAvailable(undefined);
            setIsWebSocketFailed(false);
            setWebSocketsData(parsedData);
          }
          setIsLoading(false);
        } catch (ex) {
          console.error("Parsing WebSocket data failed:", ex); // Log JSON parse errors
          setIsLoading(false);
          setIsDataAvailable(
            "Something went wrong (Check Firing order in the Configuration)"
          );
          setIsWebSocketFailed(false);
        }
      } else {
        setIsDataAvailable(
          "Process file not found on server. Please check the configuration (Check Vib360 is running or not)"
        );
        setIsWebSocketFailed(false);
        setWebSocketsData({});
      }
    }
  }, [lastMessage]);

  useEffect(() => {
    if (licenseInfo && licenseStatus && intervalHandle) {
      // @ts-expect-error
      intervalHandle.current = setInterval(() => {
        const expiryDate = licenseInfo?.expiryDate;
        if (
          expiryDate !== undefined &&
          String(expiryDate)?.trim()?.length >= 0
        ) {
          const parsedExpiryDate = dateFns.parse(
            expiryDate,
            "yyyy-MM-dd HH:mm:ss",
            new Date()
          );

          const currentDate = new Date();
          const fifteenDaysBeforeExpiry = dateFns.subDays(parsedExpiryDate, 15);
          if (dateFns.isBefore(fifteenDaysBeforeExpiry, currentDate)) {
            // in 15 days range
            setShowLicenseExpiryMsg(true);
            setLicExpiryText(
              dateFns.format(parsedExpiryDate, "dd MMM yyyy, H:mm a")
            );
          } else {
            setShowLicenseExpiryMsg(false);
            setLicExpiryText("");
          }
        }
      }, 3000);
    }
    return () => {
      if (intervalHandle.current) {
        clearInterval(intervalHandle.current);
      }
    };
  }, [licenseInfo, licenseStatus, intervalHandle]);

  useEffect(() => {
    setIsLoading(true);
    DashboardApi.getModules()
      .then((res) => {
        if (res.length) setModuleTabs(res || []);
        else {
          setIsLoading(false);
        }
      })
      .catch((e) => {
        setModuleTabs([]);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (moduleTabs && moduleTabs.length > 0) {
      DashboardApi.getModuleStatus(moduleTabs[activeModule].id).then((response) => {
        setModuleStatus(response.status);
      }).catch(err => {
        console.error(err.message);
      })
    }
  }, [activeModule, moduleTabs]);

  const onActiveModuleChange = (event: any, params: any) => {
    setTrendsDays(1);
    setActiveModule(params);
  };
  const handleClick = async () => {
    api.get(
      `${window.location.origin}/client-portal-api/app/start_dashboard_socket.php`
    );

    api.get(
      `${window.location.origin}/client-portal-api/app/start_signal_socket.php`
    );

    api.get(
      `${window.location.origin}/client-portal-api/app/start_status_socket.php`
    );

    await addSystemStatusLog("Restart Websocket", "Success");
    window.location.reload();
  };

  const rebootSystem = () => {
    setIsSystemRebootLoading(true);
    api
      .get("/vbox/restart.php")
      .then(() => {
        addSystemStatusLog("System Reboot", "Success");
        setRebootConfirmOpen(false);
      })
      .catch((error) => {
        addSystemStatusLog("System Reboot", "Failed");
        console.error(error);
      })
      .finally(() => {
        setIsSystemRebootLoading(false);
      });
  };

  const addSystemStatusLog = async (type: string, status: string) => {
    try {
      const res = await api.post("/system_status_logs/addSystemStatusLog.php", {
        created_by: sessionStorage.getItem(USER_NAME_ROLE) == "admin" ? 1 : 2,
        status,
        type,
      });

      console.log("System status log added successfully:", res.data);
      return res;
    } catch (error) {
      console.error("Error adding system status log:", error);
      throw error;
    }
  };

  return (
    <Box>
      {showLicenseExpiryMsg && (
        <Box sx={{ my: 1 }}>
          <Alert
            severity="warning"
            onClose={() => {
              clearInterval(intervalHandle.current);
              setShowLicenseExpiryMsg(false);
            }}
          >
            <AlertTitle>
              {t("dashboard.lic.alert.title", { ns: "dashboard" })}
            </AlertTitle>
            <Typography variant="caption" component={"span"}>
              {t("dashboard.lic.alert.text.part1", { ns: "dashboard" })}{" "}
              {licExpiryText}
            </Typography>
          </Alert>
        </Box>
      )}
      {isLoading && (
        <Box sx={{ my: 1 }}>
          <LinearProgress />
        </Box>
      )}
      {isDataAvailable && (
        <Box sx={{ my: 1 }}>
          <Alert
            sx={{ display: "flex" }}
            severity="error"
            onClose={() => {
              clearInterval(intervalHandle.current);
              setIsDataAvailable(false);
              setIsWebSocketFailed(false);
            }}
          >
            <Box sx={{ display: "flex" }}>
              <AlertTitle>{isDataAvailable}</AlertTitle>
              {isWebSocketFailed && (
                <Button
                  size="small"
                  sx={{ ml: "30px" }}
                  variant="outlined"
                  onClick={handleClick}
                >
                  Restart WebSocket
                </Button>
              )}
            </Box>
          </Alert>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("dashboard.heading.text", { ns: "dashboard" })}
        </Typography>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Typography component="span" variant="body1">
            {isLoading
              ? t("dashboard.loading.module.text", { ns: "dashboard" })
              : t("dashboard.module.text", { ns: "dashboard" })}
          </Typography>
          <Box sx={{ minWidth: "50vw", maxWidth: "50vw" }}>
            <Tabs
              value={activeModule}
              onChange={onActiveModuleChange}
              aria-label="select modules"
              classes={{
                root: classes.tabsRoot,
                indicator: classes.tabIndicator,
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {moduleTabs?.map((tabElement: any, index: number) => (
                <Tooltip
                  title={tabElement.name}
                  key={`toolTip${tabElement?.id}`}
                >
                  <Tab
                    label={
                      <Box>
                        {JSON.parse(tabElement.from_data).asset_name} -
                        {JSON.parse(tabElement.from_data).equipment_name}
                      </Box>
                    }
                    {...tabProps(index)}
                    classes={{
                      root: classes.tabRoot,
                      selected: classes.activeTab,
                    }}
                  />
                </Tooltip>
              ))}
            </Tabs>
          </Box>
          <Tooltip title="Reboot the Vibox before a trial if readings or dashboard trends are not available.">
            <Button
              size="small"
              variant="outlined"
              aria-label="System Reboot"
              startIcon={
                isSystemRebootLoading ? <CircularProgress size={12} /> : <RestartAltIcon />
              }
              onClick={() => setRebootConfirmOpen(true)}
              disabled={isSystemRebootLoading}
              sx={{
                color: "text.secondary",
                borderColor: "divider",
                whiteSpace: "nowrap",
                minWidth: "auto",
                px: 1,
                "&:hover": {
                  color: "error.main",
                  borderColor: "error.main",
                  bgcolor: "transparent",
                },
              }}
            >
              System Reboot
            </Button>
          </Tooltip>
        </Box>
      </Box>
      <Dialog
        open={rebootConfirmOpen}
        onClose={() => setRebootConfirmOpen(false)}
      >
        <DialogTitle>System Reboot</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will reboot the Vibox. After reboot, the system may require
            5-8 minutes for new readings and dashboard trends to become
            available.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRebootConfirmOpen(false)}
            disabled={isSystemRebootLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={rebootSystem}
            variant="contained"
            color="error"
            disabled={isSystemRebootLoading}
            startIcon={
              isSystemRebootLoading ? <CircularProgress size={10} /> : <RestartAltIcon />
            }
          >
            Reboot System
          </Button>
        </DialogActions>
      </Dialog>

      {moduleTabs?.map((item: any, index: any) => (
        <TabPanel key={item.id} value={activeModule} index={index}>
          <TabModuleRender
            moduleId={item.id}
            configurationId={item.configuration_id}
            type={item.module_type}
            processName={item.process_name}
            formData={item.from_data}
            moduleData={webSocketsData}
            classes={classes}
            trendsData={trendsData}
            trendsDays={trendsDays}
            onTrendsDaysChange={setTrendsDays}
            moduleType={item.module_type}
            status={moduleStatus}
          />
        </TabPanel>
      ))}
    </Box>
  );
};
export default DashboardPage;
